import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, type IconName } from '@frontend/icons';
import { SettingsLink } from '@frontend/settings-routing';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';

export interface SettingsItem {
  id: string;
  title: string;
  to: string;
  group: string;
  show: boolean;
  search?: Record<string, any>;
  caretIcon?: React.ReactNode;
  iconName?: IconName;
  trackingId?: string;
}

/**
 * Manages user-bookmarked phone settings, including cleanup of stale bookmarks and logic to determine if we should
 * show the "new" notification.
 *
 * This hook provides a comprehensive interface for handling bookmarked phone settings. It persists the user's
 * bookmarks using the `usePreference` hook. It also includes logic to clean up any bookmarked settings
 * that are no longer available (stale bookmarks) by comparing them against the provided `allAvailableIds`.
 *
 * Additionally, it manages the visibility of a notification intended to introduce the bookmark feature to users.
 * The notification is shown for a maximum of 5 different days and is permanently hidden once the user
 * creates their first bookmark or if they already have existing bookmarks.
 *
 * @param allAvailableIds - An array of strings representing all currently available phone setting IDs. This is used to identify and remove stale bookmarks.
 * @returns An object containing:
 *  - `bookmarkedIds`: A `Set<string>` of the currently bookmarked setting IDs for efficient lookups.
 *  - `toggleBookmark`: A function `(id: string) => void` to add or remove a setting ID from the bookmarks.
 *  - `shouldShowNotification`: A boolean indicating whether the feature-discovery notification should be displayed to the user.
 */
export const useBookmarks = (allAvailableIds: string[]) => {
  const [bookmarkedSettings, setBookmarkedSettings] = usePreference('settingsBookmarks');

  const bookmarkedIds = useMemo(() => {
    const phoneSettingsBookmarks = bookmarkedSettings?.['phone-settings'];
    return phoneSettingsBookmarks ? new Set<string>(phoneSettingsBookmarks) : new Set<string>();
  }, [bookmarkedSettings]);

  // Clean up stale bookmarks that no longer exist in settings
  useEffect(() => {
    if (!bookmarkedSettings?.['phone-settings'] || allAvailableIds.length === 0) return;
    const allAvailableIdsSet = new Set(allAvailableIds);

    const currentBookmarks = bookmarkedSettings?.['phone-settings'] || [];
    const staleBookmarks = currentBookmarks.filter((id) => !allAvailableIdsSet.has(id));

    if (staleBookmarks.length > 0) {
      const cleanedBookmarks = currentBookmarks.filter((id) => allAvailableIdsSet.has(id));
      setBookmarkedSettings({
        ...bookmarkedSettings,
        'phone-settings': cleanedBookmarks,
      });
    }
  }, [allAvailableIds, bookmarkedSettings, setBookmarkedSettings]);

  const toggleBookmark = (id: string) => {
    const currentBookmarks = bookmarkedSettings?.['phone-settings'] || [];
    const bookmarkSet = new Set(currentBookmarks);

    if (bookmarkSet.has(id)) {
      bookmarkSet.delete(id);
    } else {
      bookmarkSet.add(id);
    }

    setBookmarkedSettings({
      ...bookmarkedSettings,
      'phone-settings': Array.from(bookmarkSet),
    });
  };

  return { bookmarkedIds, toggleBookmark };
};

interface BookmarkedSettingsProps {
  bookmarkedItems: SettingsItem[];
  disabled?: boolean;
  trackingIdPrefix?: string;
  toggleBookmark: (id: string) => void;
}

export const BookmarkedSettings = ({
  bookmarkedItems,
  disabled,
  trackingIdPrefix,
  toggleBookmark,
}: BookmarkedSettingsProps) => {
  const { t } = useTranslation('phone');

  return (
    <div
      css={css`
        margin-bottom: ${theme.spacing(4)};
        max-width: 700px;
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(1)};
      `}
    >
      <Heading level={3}>{t('My Bookmarks ({{count}})', { count: bookmarkedItems.length })}</Heading>
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr;
          gap: ${theme.spacing(1)};
          max-width: 100%;

          @media (min-width: 520px) {
            grid-template-columns: repeat(2, 1fr);
          }

          @media (min-width: 700px) {
            grid-template-columns: repeat(3, 1fr);
          }
        `}
      >
        {bookmarkedItems.map((item) => (
          <SettingsLink
            key={item.id}
            // @ts-expect-error - Dynamic route
            to={item.to}
            search={item.search}
            disabled={disabled}
            trackingId={`${trackingIdPrefix}_bookmark-setting-card`}
            css={css`
              background: ${theme.colors.white};
              border: 1px solid ${theme.colors.neutral[20]};
              border-radius: ${theme.borderRadius.small};
              transition: all 0.2s ease;
              cursor: pointer;
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(1)};
              text-decoration: none;
              color: inherit;
              opacity: ${disabled ? 0.5 : 1};
              pointer-events: ${disabled ? 'none' : 'auto'};

              &:hover {
                background-color: ${theme.colors.neutral5};
              }
            `}
          >
            {/* Header row with icon and bookmark button */}
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                // Added padding here so that the padding from the bookmark button aligns to the right edge of the card
                padding: ${theme.spacing(2, 0, 0, 2)};
              `}
            >
              <Icon color='subdued' name={item.iconName || 'settings'} size={18} />
              <Button
                variant='tertiary'
                size='small'
                hoverLabel={t('Remove Bookmark')}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleBookmark(item.id);
                }}
                trackingId={`${trackingIdPrefix}_bookmark-card_remove-bookmark-button`}
                aria-label={t('Remove bookmark')}
              >
                <Icon name='bookmark-filled' size={16} />
              </Button>
            </div>

            {/* Title */}
            <Text
              weight='bold'
              size='medium'
              css={css`
                // Added padding here to make up for the lack of padding in the parent card so that
                // we can align the bookmark button to the edge (see note above)
                padding: ${theme.spacing(0, 2, 2, 2)};
              `}
            >
              {item.title}
            </Text>
          </SettingsLink>
        ))}
      </div>
    </div>
  );
};
